var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { SceneLoader as BABYLON_SceneLoader } from '@inter-ikea-betrakta-babylonjs/core/Loading/sceneLoader.js';
import { SceneAssetInstance } from './SceneAssetInstance.js';
import { SceneAssetContainer } from './SceneAssetContainer.js';
export class SceneAsset {
    constructor(scene) {
        this._asset_container = null;
        this._instances = [];
        this._asset_generation = 0;
        // Custom promise setup. Just using a regular promise doesn't really work with multiple users.
        this._current_loading_promise = null;
        this._current_loading_promise_failed = false;
        this.scene = scene;
    }
    load_meshes(meshes) {
        this._asset_generation++;
        this._asset_container = new SceneAssetContainer(this.scene.viewer, meshes);
        this.update_instances();
    }
    load_mesh_from_url_internal(url, extension) {
        return __awaiter(this, void 0, void 0, function* () {
            // Increment generation and store the current generation
            this._asset_generation++;
            const generation_at_load_request = this._asset_generation;
            const asset_path = url.split('/');
            const asset_file = asset_path.pop();
            try {
                const loaded_asset = yield BABYLON_SceneLoader.LoadAssetContainerAsync(asset_path.join('/') + '/', asset_file, this.scene.babylon_scene, null, extension);
                // After await, if the generation we got when starting this function doesn't match up, disregard
                if (this._asset_generation !== generation_at_load_request) {
                    loaded_asset.dispose();
                    return;
                }
                this._asset_container = new SceneAssetContainer(this.scene.viewer, loaded_asset);
                this.update_instances();
                this.scene.viewer.mark_scene_dirty();
                this._current_loading_promise_failed = false;
                this._current_loading_promise_error = null;
            }
            catch (error) {
                this._current_loading_promise_error = error;
                this._current_loading_promise_failed = true;
            }
        });
    }
    load_mesh_from_url(url, extension) {
        return __awaiter(this, void 0, void 0, function* () {
            this._current_loading_promise = this.load_mesh_from_url_internal(url, extension);
            return this._current_loading_promise;
        });
    }
    get_loaded_promise() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._current_loading_promise === null)
                return Promise.resolve();
            yield this._current_loading_promise;
            if (this._current_loading_promise_failed) {
                return Promise.reject(this._current_loading_promise_error);
            }
            else {
                return Promise.resolve();
            }
        });
    }
    update_instances() {
        if (this._asset_container) {
            this._instances.forEach((instance) => this._asset_container.sync_instance(instance, this.use_flattened_gltfs));
        }
    }
    instance_disposed(instance) {
        const index = this._instances.indexOf(instance);
        if (index >= 0) {
            this._instances.splice(index, 1);
        }
    }
    dispose() {
        var _a;
        this._instances.forEach((instance) => {
            instance.dispose_mesh();
        });
        (_a = this._asset_container) === null || _a === void 0 ? void 0 : _a.dispose();
        this._asset_container = null;
    }
    /**
     * Creates a new instance of this SceneAsset which will be displayed in the scene.
     * @returns The created SceneAssetInstance
     */
    create_instance(model_transform) {
        var _a;
        const instance = new SceneAssetInstance(this.scene, this, model_transform);
        (_a = this._asset_container) === null || _a === void 0 ? void 0 : _a.sync_instance(instance, this.use_flattened_gltfs);
        this._instances.push(instance);
        return instance;
    }
    /**
     * @returns The number of instances of this SceneAsset that are in the scene
     */
    get_instance_count() {
        return this._instances.length;
    }
    /**
     * The container which holds the currently loaded source data (such as geometry, materials, etc) for this asset
     */
    get asset_container() {
        return this._asset_container;
    }
    get use_flattened_gltfs() {
        var _a;
        if ((_a = this._asset_container) === null || _a === void 0 ? void 0 : _a.has_animations()) {
            return false;
        }
        else {
            return true;
        }
    }
}
//# sourceMappingURL=SceneAsset.js.map